import { useQuery, useMutation, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { dataverseClient, TABLES, bffFetch } from '@shared/api';
import { escapeODataString } from '@shared/api/dataverse/utils';
import { assertGuid } from '@shared/lib';
import { autoStampGroup } from '@entities/groupdocs-sites';
import { useAuth } from '@shared/providers/AuthProvider';
import { useOrgContext } from '@shared/providers';
import type { OpsGroup, OpsGroupQueryParams } from '../model/types';

// =============================================================================
// Entity Interface
// =============================================================================

interface OpsGroupEntity {
  vba_opsgroupid: string;
  vba_name: string;
  _vba_account_value?: string;
  vba_accountnumber?: string;
  vba_city?: string;
  vba_state?: string;
  vba_enrolledon?: string;
  _vba_enrolledby_value?: string;
  _ownerid_value?: string;
  vba_isactive?: boolean;
  // Why this group left, in plain words. Vera's note about the CRM account's
  // dts_termdate, which stays the source of record for WHEN. Reads undefined
  // on any org where the column isn't provisioned yet (the list query doesn't
  // $select), so reads are always safe; a WRITE to such an org 400s.
  vba_termreason?: string;
  vba_notes?: string;
  vba_othergroupnames?: string;
  vba_contracturl?: string;
  vba_implementationphase?: number;
  vba_historicaldataverified?: boolean;
  vba_verifiedby?: string;
  vba_verifiedon?: string;
  vba_donotcontact?: boolean;
  createdon?: string;
  modifiedon?: string;
  // Expanded Account fields. ownerid here is the Dynamics Account Owner —
  // the actual Account Exec — distinct from this row's _ownerid_value
  // (which is just "who created the OpsGroup row in Vera"; not meaningful
  // as an AE signal, especially for bulk-migrated rows which all share
  // the migration runner as their record owner).
  vba_Account?: {
    // Account Number + Location read LIVE from the linked CRM account — they
    // live ONLY in CRM, never snapshotted onto the OpsGroup (a snapshot goes
    // stale on merge/re-point; see project_crm_match). Legal Name (vba_name)
    // IS an intentional Vera-maintained snapshot, so it stays on the row.
    accountnumber?: string;
    address1_city?: string;
    address1_stateorprovince?: string;
    address1_line1?: string;
    address1_postalcode?: string;
    telephone1?: string;
    dts_effectivedate?: string;
    dts_termdate?: string;
    _ownerid_value?: string;
    '_ownerid_value@OData.Community.Display.V1.FormattedValue'?: string;
    // Deep expand: broker contact + their parent agency. Used by the
    // Groups list to render "Broker (Agency)" in one fetch instead of
    // chasing the contact lookup row-by-row.
    dts_BrokerOfRecord?: {
      contactid: string;
      fullname?: string;
      parentcustomerid_account?: {
        accountid: string;
        name?: string;
        // One level deeper: the agency account's own parent account
        // (the "agency parent" — e.g. Acrisure over a local office).
        parentaccountid?: {
          accountid: string;
          name?: string;
        };
      };
    };
  };
  [key: string]: unknown;
}

// =============================================================================
// Mappers
// =============================================================================

function mapToDomain(entity: OpsGroupEntity): OpsGroup {
  return {
    id: entity.vba_opsgroupid,
    name: entity.vba_name ?? '',
    accountId: entity._vba_account_value ?? '',
    accountName: (entity['_vba_account_value@OData.Community.Display.V1.FormattedValue'] as string) ?? undefined,
    // Live from the linked account — CRM is the only home for these.
    accountNumber: entity.vba_Account?.accountnumber ?? undefined,
    city: entity.vba_Account?.address1_city ?? undefined,
    state: entity.vba_Account?.address1_stateorprovince ?? undefined,
    street: entity.vba_Account?.address1_line1 ?? undefined,
    postalCode: entity.vba_Account?.address1_postalcode ?? undefined,
    phone: entity.vba_Account?.telephone1 ?? undefined,
    enrolledOn: entity.vba_enrolledon ?? undefined,
    enrolledById: entity._vba_enrolledby_value ?? undefined,
    enrolledByName: (entity['_vba_enrolledby_value@OData.Community.Display.V1.FormattedValue'] as string) ?? undefined,
    isActive: entity.vba_isactive ?? true,
    termReason: entity.vba_termreason ?? undefined,
    notes: entity.vba_notes ?? undefined,
    otherGroupNames: entity.vba_othergroupnames ?? undefined,
    contractUrl: entity.vba_contracturl ?? undefined,
    implementationPhase: (entity.vba_implementationphase as OpsGroup['implementationPhase']) ?? undefined,
    // Reads false until vba_historicaldataverified is provisioned — the list
    // query doesn't $select columns, so an absent column is simply undefined.
    verified: entity.vba_historicaldataverified ?? false,
    verifiedBy: entity.vba_verifiedby ?? undefined,
    verifiedOn: entity.vba_verifiedon ?? undefined,
    // Same unprovisioned-column safety as verified: absent reads false.
    doNotContact: entity.vba_donotcontact ?? false,
    // Account Exec = the Account's owner in Dynamics, not the OpsGroup
    // row's owner. Fall back to the OpsGroup row owner only as a last
    // resort (shouldn't happen — every OpsGroup has an Account FK).
    ownerName:
      (entity.vba_Account?.['_ownerid_value@OData.Community.Display.V1.FormattedValue'] as string)
      ?? (entity['_ownerid_value@OData.Community.Display.V1.FormattedValue'] as string)
      ?? undefined,
    // Stable AE join key (the account's owner id), already in EXPAND_ACCOUNT.
    ownerId: entity.vba_Account?._ownerid_value ?? undefined,
    effectiveDate: entity.vba_Account?.dts_effectivedate ?? undefined,
    termDate: entity.vba_Account?.dts_termdate ?? undefined,
    brokerName: entity.vba_Account?.dts_BrokerOfRecord?.fullname ?? undefined,
    brokerContactId: entity.vba_Account?.dts_BrokerOfRecord?.contactid ?? undefined,
    brokerAgencyId: entity.vba_Account?.dts_BrokerOfRecord?.parentcustomerid_account?.accountid ?? undefined,
    brokerAgencyName: entity.vba_Account?.dts_BrokerOfRecord?.parentcustomerid_account?.name ?? undefined,
    brokerAgencyParentId: entity.vba_Account?.dts_BrokerOfRecord?.parentcustomerid_account?.parentaccountid?.accountid ?? undefined,
    brokerAgencyParentName: entity.vba_Account?.dts_BrokerOfRecord?.parentcustomerid_account?.parentaccountid?.name ?? undefined,
    createdOn: entity.createdon ?? undefined,
    modifiedOn: entity.modifiedon ?? undefined,
  };
}

function mapToEntity(input: Partial<OpsGroup>, accountId?: string): Record<string, unknown> {
  const entity: Record<string, unknown> = {};

  if (input.name !== undefined) entity.vba_name = input.name;
  // Account Number + Location are CRM-only — never written back to the
  // OpsGroup row (they're read live from the linked account). Legal Name
  // (vba_name) is the one intentional Vera-maintained snapshot.
  if (input.isActive !== undefined) entity.vba_isactive = input.isActive;
  if (input.termReason !== undefined) entity.vba_termreason = input.termReason;
  if (input.notes !== undefined) entity.vba_notes = input.notes;
  if (input.otherGroupNames !== undefined) entity.vba_othergroupnames = input.otherGroupNames;
  if (input.contractUrl !== undefined) entity.vba_contracturl = input.contractUrl;
  if (input.implementationPhase !== undefined) entity.vba_implementationphase = input.implementationPhase;
  if (input.verified !== undefined) entity.vba_historicaldataverified = input.verified;
  if (input.verifiedBy !== undefined) entity.vba_verifiedby = input.verifiedBy;
  if (input.verifiedOn !== undefined) entity.vba_verifiedon = input.verifiedOn;
  if (input.doNotContact !== undefined) entity.vba_donotcontact = input.doNotContact;

  const acctId = accountId ?? input.accountId;
  if (acctId) entity['vba_Account@odata.bind'] = `/accounts(${assertGuid(acctId, 'accountId')})`;

  return entity;
}

// =============================================================================
// API Functions
// =============================================================================

async function list(params: OpsGroupQueryParams = {}): Promise<OpsGroup[]> {
  const filters: string[] = [];
  if (params.activeOnly) filters.push('vba_isactive eq true');
  if (params.nameFilter) {
    filters.push(`contains(vba_name, '${escapeODataString(params.nameFilter)}')`);
  }

  const response = await dataverseClient.list<OpsGroupEntity>(TABLES.OPS_GROUP, {
    filter: filters.length > 0 ? filters.join(' and ') : undefined,
    orderBy: 'vba_name asc',
    expand: EXPAND_ACCOUNT,
  });

  return response.value.map(mapToDomain);
}

// Deep expand: vba_Account → broker contact → parent agency account →
// agency's parent account. The Groups list + Advanced Search consume
// brokerName + brokerAgencyName + brokerAgencyParentName from here.
const EXPAND_ACCOUNT = [
  'vba_Account($select=accountnumber,address1_city,address1_stateorprovince,address1_line1,address1_postalcode,telephone1,dts_effectivedate,dts_termdate,_ownerid_value' +
  ';$expand=dts_BrokerOfRecord($select=contactid,fullname' +
  ';$expand=parentcustomerid_account($select=accountid,name' +
  ';$expand=parentaccountid($select=accountid,name))))',
];

async function get(id: string): Promise<OpsGroup> {
  const entity = await dataverseClient.get<OpsGroupEntity>(TABLES.OPS_GROUP, id, {
    expand: EXPAND_ACCOUNT,
  });
  return mapToDomain(entity);
}

async function getByAccountId(accountId: string): Promise<OpsGroup | null> {
  const response = await dataverseClient.list<OpsGroupEntity>(TABLES.OPS_GROUP, {
    filter: `_vba_account_value eq '${assertGuid(accountId, 'accountId')}'`,
    top: 1,
    expand: EXPAND_ACCOUNT,
  });
  return response.value.length > 0 ? mapToDomain(response.value[0]) : null;
}

async function create(input: Partial<OpsGroup>): Promise<OpsGroup> {
  // Every OpsGroup MUST anchor to a Dynamics account — Vera's data model is
  // account-anchored (contract years, GI, partner IDs all filter by account).
  // Refuse to create an orphan: a blank accountId silently produced an
  // account-less group whose account-scoped tabs fall back to org-wide/empty
  // queries (e.g. Partner IDs listing every group's IDs). Throwing here is the
  // root-cause guard; callers (EnrollmentDrawer, EnrollGroupDialog) surface it.
  assertGuid(input.accountId ?? '', 'accountId');
  const entity = mapToEntity(input, input.accountId);
  // Stamp enrollment date
  entity.vba_enrolledon = new Date().toISOString().split('T')[0];
  const response = await dataverseClient.create<OpsGroupEntity>(TABLES.OPS_GROUP, entity);
  return mapToDomain(response);
}

async function update(id: string, input: Partial<OpsGroup>): Promise<void> {
  const entity = mapToEntity(input);
  await dataverseClient.update(TABLES.OPS_GROUP, id, entity);
}

async function remove(id: string): Promise<void> {
  await dataverseClient.remove(TABLES.OPS_GROUP, id);
}

// =============================================================================
// React Query Hooks
// =============================================================================

const keys = {
  all: ['opsGroups'] as const,
  lists: () => [...keys.all, 'list'] as const,
  list: (params: OpsGroupQueryParams) => [...keys.lists(), params] as const,
  detail: (id: string) => [...keys.all, 'detail', id] as const,
  byAccount: (accountId: string) => [...keys.all, 'byAccount', accountId] as const,
};

export function useOpsGroups(
  params: OpsGroupQueryParams = {},
  options?: Omit<UseQueryOptions<OpsGroup[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: keys.list(params),
    queryFn: () => list(params),
    ...options,
  });
}

export function useOpsGroup(id: string) {
  return useQuery({
    queryKey: keys.detail(id),
    queryFn: () => get(id),
    enabled: !!id,
  });
}

export function useOpsGroupByAccountId(accountId: string) {
  return useQuery({
    queryKey: keys.byAccount(accountId),
    queryFn: () => getByAccountId(accountId),
    enabled: !!accountId,
  });
}

/**
 * Create an Ops group (enrollment). Both front doors use this one mutation -
 * the Groups page Enroll dialog and the intake Enrollment drawer - which is why
 * the auto-stamp hook lives here rather than in either page (ADO #1488).
 *
 * The stamp is fire-and-forget and deliberately unawaited: whether it happens
 * at all is a server-side setting operations owns (off until they turn it on),
 * a fresh SharePoint site takes minutes, and enrollment must never fail or wait
 * because of it. When auto-stamp is off, the call is a cheap no-op answer.
 */
export function useCreateOpsGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: create,
    onSuccess: (group) => {
      qc.invalidateQueries({ queryKey: keys.all });
      if (group?.id) void autoStampGroup(group.id);
    },
  });
}

export function useUpdateOpsGroup() {
  const qc = useQueryClient();
  const { displayName } = useAuth();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<OpsGroup> }) => {
      // Stamp who/when when a group's history is verified (verified→true).
      const stamped =
        input.verified === true
          ? { ...input, verifiedBy: displayName ?? 'Unknown', verifiedOn: new Date().toISOString() }
          : input;
      return update(id, stamped);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: keys.all }); },
  });
}

/**
 * Deactivate / reactivate a group through the BFF destructive endpoint rather
 * than a client-side PATCH, so the capability is checked server-side under the
 * BFF identity and the flip lands in the audit log (ADO #1246).
 *
 * Every OTHER vba_opsgroup write still goes through useUpdateOpsGroup with the
 * user's delegated token; only isActive is routed here. Do not fold this back
 * into useUpdateOpsGroup, it would push four unrelated everyday flows (edit
 * dialog, phase, verified, teammate write-through) behind an admin capability.
 */
export function useSetOpsGroupActive() {
  const qc = useQueryClient();
  const { activeOrg } = useOrgContext();
  return useMutation({
    mutationFn: async (
      { id, isActive, termReason }: { id: string; isActive: boolean; termReason?: string },
    ) => {
      const res = await bffFetch(`/api/ops/groups/${assertGuid(id, 'id')}/set-active`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // termReason rides the same call so the shelf flag and the reason are
        // written in one PATCH. The BFF ignores it on reactivate (and clears
        // the column), so callers do not have to.
        body: JSON.stringify({ org: activeOrg, isActive, termReason }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(
          body.error ?? `Group ${isActive ? 'reactivate' : 'deactivate'} failed (${res.status})`,
        );
      }
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: keys.all }); },
  });
}

/**
 * Set or clear the group's term date, the CRM account's dts_termdate, through
 * the BFF term endpoint. The BFF PATCHes the ACCOUNT row under the service
 * identity (Ops users hold Write on vba_opsgroup, not on account) after a
 * server-side ops.term-group capability check, and audits the change.
 *
 * termDate is 'YYYY-MM-DD' to set, or explicit null to clear: the mappers-
 * skip-undefined rule means only a literal null reaches Dataverse as a clear,
 * and the BFF 400s an absent key on purpose. Invalidates the whole group
 * query root so the derived lifecycle (active / terming / termed) refreshes
 * everywhere it is computed.
 */
export function useSetOpsGroupTermDate() {
  const qc = useQueryClient();
  const { activeOrg } = useOrgContext();
  return useMutation({
    mutationFn: async ({ id, termDate }: { id: string; termDate: string | null }) => {
      const res = await bffFetch(`/api/ops/groups/${assertGuid(id, 'id')}/term`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ org: activeOrg, termDate }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(
          body.error ?? `Term date ${termDate ? 'save' : 'clear'} failed (${res.status})`,
        );
      }
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: keys.all }); },
  });
}

export function useDeleteOpsGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: remove,
    onSuccess: () => { qc.invalidateQueries({ queryKey: keys.all }); },
  });
}
